/**
 * OBJ-135/FIND-198 (TG-5 content criterion -> AC-9): the one source for
 * the "a working-copy read failed" string set, consumed by every AC-9
 * failed-read surface — `App.tsx`'s `InlineReloadFailure` and
 * `buildReloadFailureBanner`, and `router.tsx`'s `RouteErrorShell`.
 *
 * CONTENT DESIGN REVIEWED, 2026-10-02 (closes the content-authoring limb
 * of OBJ-135/FIND-198; the structural consolidation above was already
 * done by the implementor and stands unchanged). Register matched to this
 * feature's other authored copy (`features/renewal-landing/content.ts`,
 * `features/declaration/content.ts`, `features/change-check/content.ts`):
 * plain, active voice ("we"), second person where natural, no jargon.
 *
 * - `title` changed from "This page could not be loaded" (passive,
 *   nobody's acting) to "We could not load this page", matching the
 *   active-voice construction `renewal-landing/content.ts`'s own
 *   `failedRead.title` ("We could not load your renewal") already
 *   established for this exact AC-9 family. `message` and `retryLabel`
 *   were reviewed and are carried forward with one change (below) — they
 *   already met the bar: plain, short, no jargon.
 * - `message` drops the "or return to the renewal overview" clause,
 *   which is the defect OBJ-135 found: it named an action with no control
 *   behind it on five call sites. The human decision (Harry Henry,
 *   2026-10-02) is to supply the control, not drop the sentiment — see
 *   `backToRenewalLabel` below — so the control's own label now carries
 *   that instruction instead of a sentence pointing at it, matching how
 *   `saveAndFinishLaterLabel` and `backLabel` across this feature's other
 *   content modules are never separately explained in body copy. The
 *   reassurance clause ("Nothing you've already confirmed has been
 *   lost") is added from `renewal-landing/content.ts`'s own
 *   `failedRead.message` verbatim: this banner covers the same failed-
 *   read/failed-reload moment one route over, for a customer the product
 *   spec describes as low-digital-literacy and anxious their confirmed
 *   work vanished (Sarah Richards step 1/2: reassurance is the essential
 *   information here, not decoration) — one voice for one moment, not two
 *   inventions of it.
 * - `backToRenewalLabel` is new: the label for the "return to the renewal
 *   overview" control the human decision requires the implementor to add
 *   (a link/button to `/`) at the five call sites named in
 *   OBJ-135/FIND-198's secondary defect. CLOSED 2026-10-05 (ADR-20261005-q7vn):
 *   `InlineReloadFailure` always carried this control; `RouteErrorShell` was
 *   the one full-page surface still missing it, now fixed.
 *   "Back to your renewal" follows
 *   this feature's established "Back to [destination]" pattern
 *   (`declaration/content.ts`'s "Back to review exposures",
 *   `change-check/content.ts`'s "Back to confirm details") and names the
 *   destination exactly as the customer will see it there
 *   (`renewal-landing/content.ts`'s own `pageHeading`, "Your renewal").
 *   This is a string only — the control itself (element, onClick/navigate
 *   wiring) is an implementation task, not authored here.
 *
 * `pendingShellCopy` below is explicitly out of this review's scope — see
 * its own doc comment.
 */
export const reloadFailureCopy = {
  title: "We could not load this page",
  message: "Something has gone wrong on our end. Nothing you've already confirmed has been lost. Try again.",
  retryLabel: "Try again",
  backToRenewalLabel: "Back to your renewal",
} as const;

/**
 * OBJ-135/FIND-198, judge turn 2 follow-up: the pending-shell label
 * `App.tsx`'s `PendingShell` and `router.tsx`'s `RouteLoadingShell` share
 * ("Loading…") was left out of the first consolidation pass — both
 * components still hand-typed their own copy of it. CONTENT DESIGN
 * REVIEWED, 2026-10-05 (ADR-20261005-q7vn): kept as "Loading…" — a
 * generic per-route transition fallback, not tied to any specific
 * read (unlike `renewal-landing/content.ts`'s own route-specific
 * "Checking your renewal..."), already plain/short/no-jargon, no
 * behavioural defect to fix.
 */
export const pendingShellCopy = {
  loadingLabel: "Loading…",
} as const;
